import { useEffect, useState } from 'react'
import { useCommonT } from '../../i18n'
import { useScanFabSuppressed } from '../../lib/scanFabSuppression'
import { useOnScreenKeyboard } from '../../hooks/useOnScreenKeyboard'
import { FAB_CLEARANCE_VALUE, FAB_CLEARANCE_VAR } from '../../lib/navV2.codes'
import GetAppSheet from './GetAppSheet'
import { ScanIcon } from './navIcons'
import { isAndroidApp } from '../../lib/androidApp'
import { useScannerLaunch } from '../../hooks/useScannerLaunch'

// The phone floating Scan button (nav v2, Keith 2026-10-17): Scan left the tab
// bar and floats bottom-right instead. Rendered by MobileNavV2, so it is gone
// whenever the tab bar is (useMobileNavSuppressed), and only when the Scan CTA
// flag is on (src/lib/scanCta.ts). It opens the Get the app sheet.
//
// Placement: 1rem above the tab bar, which itself rides --ad-inset-bottom and
// the safe area, so the button always clears the adhesion ad. z-40 keeps it
// under the tab bar's own layer (z-50), every sheet and overlay (z-[60]+) and
// the toasts (z-[100]); the Deck Chat pill, which owns this corner on phones,
// hides it while mounted (src/lib/scanFabSuppression.ts). 56px circle, above
// the 44px minimum target.
//
// It also steps aside while the on-screen keyboard is (probably) up, so it
// never covers a composer's Send button (useOnScreenKeyboard). While it IS on
// screen it publishes --fab-clearance on <html> (its footprint: 8rem above the
// tab bar's base, plus the safe area and the ad inset, plus a little air), and
// the footer pads its last row by that, so Privacy choices and the language
// control can always scroll clear of it. Hidden, the variable is removed (0).

const FAB_CLASSES = 'md:hidden fixed right-4 bottom-[calc(4.5rem+env(safe-area-inset-bottom,0px)+var(--ad-inset-bottom,0px))] z-40 flex h-14 w-14 items-center justify-center rounded-full bg-purple-600 text-white shadow-[0_8px_24px_rgba(147,51,234,0.45)] transition-colors hover:bg-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-purple-300'

export default function ScanFab() {
  const t = useCommonT()
  const [sheetOpen, setSheetOpen] = useState(false)
  const suppressed = useScanFabSuppressed()
  const keyboard = useOnScreenKeyboard()
  const visible = !suppressed && !keyboard
  const android = isAndroidApp()
  // The intent (targets + launch nonce) is built at click time.
  const scanner = useScannerLaunch(android)
  useEffect(() => {
    if (!visible) return
    const root = document.documentElement
    root.style.setProperty(FAB_CLEARANCE_VAR, FAB_CLEARANCE_VALUE)
    return () => { root.style.removeProperty(FAB_CLEARANCE_VAR) }
  }, [visible])
  if (!visible) return sheetOpen ? <GetAppSheet onClose={() => setSheetOpen(false)} /> : null
  // Inside our Android app the button IS the scanner: the same intent URL as
  // /collection's Scan cards, never the get-the-iPhone-app sheet.
  if (android) {
    return (
      <a
        href={scanner.href}
        onClick={scanner.onClick}
        data-testid="scan-fab"
        aria-label={t('nav.scan_cards')}
        className={FAB_CLASSES}
      >
        <ScanIcon className="w-7 h-7" />
      </a>
    )
  }
  return (
    <>
      <button
        type="button"
        data-testid="scan-fab"
        onClick={() => setSheetOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('nav.scan_cards')}
        className={FAB_CLASSES}
      >
        <ScanIcon className="w-7 h-7" />
      </button>
      {sheetOpen && <GetAppSheet onClose={() => setSheetOpen(false)} />}
    </>
  )
}
